// Bays: optional (Settings → Bays). Name your spots, put trucks in them, see the whole shop on the Bay board.
import { useEffect, useState } from 'react';
import { api, useApi, navigate, type Row } from '../lib';
import { Button, Panel, Loading, ErrorBox, Badge, Icon, useToast, TechChip } from '../ui';

export type Bay = { id: string; name: string; capacity: number; used?: number };

/** Bays list, cached for the session (used in forms all over). */
export function useBays() {
  const { data, reload } = useApi<{ on: boolean; bays: Bay[] }>('/api/bays');
  return { on: !!data?.on, bays: data?.bays || [], reload, loaded: !!data };
}

export function BaySelect({ value, onChange, label = 'Bay', name = 'bay_id' }: { value?: string | null; onChange?: (id: string) => void; label?: string; name?: string }) {
  const { on, bays } = useBays();
  if (!on || !bays.length) return null;
  return (
    <label className="field"><span>{label} <small className="muted">(optional)</small></span>
      <select name={name} {...(onChange ? { value: value || '', onChange: e => onChange(e.target.value) } : { defaultValue: value || '' })}>
        <option value="">Waiting / not in a bay</option>
        {bays.map(b => <option key={b.id} value={b.id}>{b.name}{b.capacity ? ` (${b.used || 0}/${b.capacity})` : ''}</option>)}
      </select></label>
  );
}

/** Settings → Bays */
export function BaysSettings() {
  const { data, reload } = useApi<{ on: boolean; bays: Bay[] }>('/api/bays');
  const [on, setOn] = useState(false);
  const [list, setList] = useState<{ id?: string; name: string; capacity: number | string }[]>([]);
  const toast = useToast();
  useEffect(() => { if (data) { setOn(data.on); setList(data.bays.length ? data.bays : [{ name: 'Bay 1', capacity: '' }, { name: 'Bay 2', capacity: '' }]); } }, [data]);
  if (!data) return <Panel title="Bays"><Loading /></Panel>;
  const set = (i: number, k: 'name' | 'capacity', v: string) => setList(x => x.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <Panel title="Bays (where each truck is)">
      <div className="pad">
        <label className="check"><input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} /> Use bays — pick a bay on each work order and see the <b>Bay board</b></label>
        {on && <>
          <p className="form-note">Name your bays however you like — Bay 1, Bay 2, Big bay, Tire bay… "Trucks that fit" is optional (it only warns when a bay is full).</p>
          <table className="bay-edit"><thead><tr><th>Bay name</th><th>Trucks that fit</th><th /></tr></thead><tbody>
            {list.map((b, i) => (
              <tr key={i}><td><input value={b.name} onChange={e => set(i, 'name', e.target.value)} placeholder="e.g. Bay 1" maxLength={40} /></td>
                <td><input value={b.capacity || ''} onChange={e => set(i, 'capacity', e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="any" /></td>
                <td><button type="button" className="icon-btn" title="Remove" onClick={() => setList(x => x.filter((_, j) => j !== i))}><Icon name="trash" size={15} /></button></td></tr>))}
          </tbody></table>
          <Button icon="plus" onClick={() => setList(x => [...x, { name: `Bay ${x.length + 1}`, capacity: '' }])}>Add bay</Button>
        </>}
        <div className="actions" style={{ marginTop: 12 }}>
          <Button kind="primary" onClick={async () => {
            try { await api('/api/bays', { method: 'PUT', body: { on, bays: list.map(b => ({ id: b.id, name: b.name, capacity: Number(b.capacity || 0) })) } }); toast('Bays saved'); reload(); setTimeout(() => location.reload(), 400); }
            catch (e) { toast((e as Error).message, 'err'); }
          }}>Save bays</Button>
        </div>
      </div>
    </Panel>
  );
}

const since = (iso: string | null | undefined, now: number) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((now - Date.parse(iso)) / 60000));
  return m < 60 ? `${m} min` : m < 60 * 24 ? `${Math.floor(m / 60)} h ${m % 60} m` : `${Math.floor(m / 1440)} day${m >= 2880 ? 's' : ''}`;
};

/** The Bay board: one column per bay, trucks as cards, drag (or "Move to") to another bay. */
export function BayBoard() {
  const { data, error, reload } = useApi<{ bays: Bay[]; trucks: Row[] }>('/api/board');
  const [drag, setDrag] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const toast = useToast();
  useEffect(() => { const t = setInterval(() => { setNow(Date.now()); if (document.visibilityState === 'visible') reload(); }, 90000); return () => clearInterval(t); }, []);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const move = async (woId: string, bayId: string | null) => {
    const bay = data.bays.find(b => b.id === bayId);
    const used = data.trucks.filter(t => t.bay_id === bayId && t.id !== woId).length;
    if (bay?.capacity && used >= bay.capacity) toast(`${bay.name} is full (${used}/${bay.capacity}) — moved anyway`, 'err');
    try { await api(`/api/work-orders/${woId}`, { method: 'PATCH', body: { bay_id: bayId } }); reload(); } catch (e) { toast((e as Error).message, 'err'); }
  };
  const cols: { id: string | null; name: string; capacity: number }[] = [{ id: null, name: 'Waiting / Yard', capacity: 0 }, ...data.bays];
  const inShop = data.trucks.length;
  const free = data.bays.reduce((a, b) => a + (b.capacity ? Math.max(0, b.capacity - data.trucks.filter(t => t.bay_id === b.id).length) : 0), 0);
  const tone = (t: Row) => (t.escalated ? 'esc' : t.priority === 'Urgent' ? 'urg' : t.status === 'Waiting Parts' ? 'wait' : t.status === 'Ready' ? 'ready' : '');
  return (
    <div className="bayboard">
      <div className="page-head">
        <div><h1>Bay board</h1><p>Every truck in the shop and where it is. Drag a truck to move it.</p></div>
        <div className="actions">
          <Button icon="maximize" onClick={() => document.documentElement.requestFullscreen?.().catch(() => {})}>Full screen</Button>
          <Button kind="primary" icon="plus" onClick={() => navigate('/work-orders?new=1')}>New work order</Button>
        </div>
      </div>
      <div className="bb-stats">
        <div><small>In the shop</small><b>{inShop} trucks</b></div>
        {data.bays.some(b => b.capacity) && <div><small>Spots free</small><b>{free}</b></div>}
        <div><small>Waiting parts</small><b>{data.trucks.filter(t => t.status === 'Waiting Parts').length}</b></div>
        <div><small>Ready to go</small><b>{data.trucks.filter(t => t.status === 'Ready').length}</b></div>
        {data.trucks.some(t => t.escalated) && <div className="esc"><small>Need specialist</small><b>{data.trucks.filter(t => t.escalated).length}</b></div>}
      </div>
      <div className="bb-cols" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(220px, 1fr))` }}>
        {cols.map(col => {
          const list = data.trucks.filter(t => (t.bay_id || null) === col.id);
          const full = col.capacity > 0 && list.length >= col.capacity;
          return (
            <div key={col.id || 'yard'} className={'bb-col' + (col.id ? '' : ' yard') + (drag ? ' dropping' : '')}
              onDragOver={e => e.preventDefault()} onDrop={() => { if (drag) void move(drag, col.id); setDrag(null); }}>
              <div className="bb-head"><b>{col.name}</b>
                <span className={'bb-cap' + (full ? ' full' : !list.length && col.id ? ' free' : '')}>{col.capacity ? `${list.length} / ${col.capacity}${full ? ' full' : ''}` : list.length || (col.id ? 'empty' : '0')}</span></div>
              {list.map(t => (
                <div key={t.id} className={'bb-card ' + tone(t)} draggable onDragStart={() => setDrag(t.id)} onDragEnd={() => setDrag(null)}>
                  <button className="bb-open" onClick={() => navigate('/work-orders/' + t.id)}>
                    <b>#{t.unit || t.unit_text || '—'}{t.unit_type ? ` · ${t.unit_type}` : ''}</b>
                    <small>{t.customer_name || 'No customer'} · {t.number}</small>
                  </button>
                  {(t.doing as Row[]).map((d, i) => <div key={i} className="bb-doing"><TechChip userId={d.user_id} name={d.name} status={d.status} /><span className="bb-jt">{d.title}</span></div>)}
                  <div className="bb-foot">
                    {t.escalated ? <Badge tone="violet">Needs specialist</Badge> : <Badge tone={t.priority === 'Urgent' ? 'red' : t.status === 'Waiting Parts' ? 'amber' : t.status === 'Ready' ? 'green' : 'blue'}>{t.priority === 'Urgent' ? `Urgent · ${t.status}` : t.status}</Badge>}
                    <small className={(now - Date.parse(t.bay_since || t.created_at)) > 86400000 ? 'danger' : 'muted'}>{since(t.bay_since || t.created_at, now)}</small>
                  </div>
                  <select className="bb-move" value={t.bay_id || ''} onChange={e => void move(t.id, e.target.value || null)} aria-label="Move to bay">
                    <option value="">Move to: Waiting / Yard</option>{data.bays.map(b => <option key={b.id} value={b.id}>Move to: {b.name}</option>)}
                  </select>
                </div>
              ))}
              {col.id && !full && col.capacity > 0 && <div className="bb-empty">{col.capacity - list.length} free spot{col.capacity - list.length > 1 ? 's' : ''}</div>}
              {!list.length && !col.capacity && <div className="bb-empty">{col.id ? 'Drop a truck here' : 'No trucks waiting'}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
